import React from 'react';
import { observer } from 'mobx-react';
import { JSONContent } from '@tiptap/react';
import { watchPreviewContent } from '@tiptap-pro/extension-snapshot';
import CircularProgress from '@mui/material/CircularProgress';

import SessionStore from 'stores/Session';
import { useReadOnlyPagesLayout } from 'containers/Tiptap/hooks/use-read-only-pages-layout';
import { useReadOnlyPreviewEditor } from 'containers/Tiptap/hooks/use-read-only-preview-editor';
import { ReadOnlyEditorContent } from 'containers/Tiptap/components/tiptap-templates/styles';
import { useCollaborationProvider } from 'containers/Tiptap/components/tiptap-templates/hooks/useCollaborationProvider';
import { EDITOR_STATUS } from 'containers/Tiptap/components/tiptap-templates/types';
import { TocProvider, useToc } from 'containers/Tiptap/components/tiptap-node/toc-node/context/toc-context';

import {
  DocBody,
  DocCard,
  DocHeader,
  LoadingWrapper,
  ProgressBar,
  ProgressLabel,
  ProgressPercent,
  ProgressTrack,
} from './styles';

const READ_THRESHOLD = 0.99;

export type AcknowledgementDocumentViewProps = {
  documentUuid: string;
  documentName: string;
  tiptapVersion: number;
  tiptapToken: string;
  getTiptapToken: () => Promise<string>;
  organisation: string;
  session: SessionStore;
  onReadComplete: () => void;
};

const AcknowledgementDocumentView = ({
  documentUuid,
  documentName,
  tiptapVersion,
  tiptapToken,
  getTiptapToken,
  organisation,
  session,
  onReadComplete,
}: AcknowledgementDocumentViewProps): React.ReactElement => {
  const { ydoc, provider, status } = useCollaborationProvider({
    uuid: documentUuid,
    session,
    tiptapToken,
    getTiptapToken,
    organisation,
    editable: false,
  });
  const { setTocContent } = useToc();

  const [previewContent, setPreviewContent] = React.useState<JSONContent | null>(null);
  const [progress, setProgress] = React.useState(0);
  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  const hasReportedRef = React.useRef(false);

  const isSynced = status === EDITOR_STATUS.SYNCED;

  // Rebuild once the ydoc has synced so the schema picks up the stored view mode (Pages).
  const { editor, contentApplied, markContentApplied } = useReadOnlyPreviewEditor(setTocContent, ydoc, [isSynced]);

  // Render the document at the exact captured version (not the latest): once the provider is
  // SYNCED, ask the collab server to preview that version and bind for its stateless reply.
  React.useEffect(() => {
    if (!provider || status !== EDITOR_STATUS.SYNCED) {
      return undefined;
    }
    provider.sendStateless(JSON.stringify({ action: 'version.preview', version: tiptapVersion }));
    const unbind = watchPreviewContent(provider, (content: JSONContent) => {
      setPreviewContent(content);
      unbind();
    });
    return () => {
      unbind();
    };
  }, [provider, status, tiptapVersion]);

  // Defer setContent out of React's commit phase — Tiptap's ReactRenderer calls flushSync to
  // mount node views synchronously, which is illegal while React is still committing
  // (rationale carried from simple-editor.tsx).
  React.useEffect(() => {
    if (!previewContent || !editor) {
      return undefined;
    }
    const id = setTimeout(() => {
      if (!editor.isDestroyed) {
        editor.commands.setContent(previewContent);
        markContentApplied();
      }
    }, 0);
    return () => clearTimeout(id);
  }, [previewContent, editor, markContentApplied]);

  useReadOnlyPagesLayout({
    editor,
    ydoc,
    contentApplied,
    variables: { documentName, documentVersion: String(tiptapVersion) },
  });

  const reportRead = React.useCallback(() => {
    if (!hasReportedRef.current) {
      hasReportedRef.current = true;
      onReadComplete();
    }
  }, [onReadComplete]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const el = event.currentTarget;
    const scrollable = el.scrollHeight - el.clientHeight;
    if (scrollable <= 0) {
      setProgress(100);
      reportRead();
      return;
    }
    const ratio = el.scrollTop / scrollable;
    setProgress(Math.min(100, Math.round(ratio * 100)));
    if (ratio >= READ_THRESHOLD) {
      reportRead();
    }
  };

  // Short-document guard: a body that isn't scrollable counts as read — but only once the editor
  // content has actually laid out. Measure on the next animation frame so we don't read a
  // pre-layout (0-height) body and report 100% on mount.
  React.useEffect(() => {
    if (!contentApplied) {
      return undefined;
    }
    const raf = requestAnimationFrame(() => {
      const el = bodyRef.current;
      if (el && el.scrollHeight - el.clientHeight <= 1) {
        setProgress(100);
        reportRead();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [contentApplied, reportRead]);

  return (
    <DocCard>
      <DocHeader>
        <ProgressLabel>Reading progress</ProgressLabel>
        <ProgressPercent>{progress}%</ProgressPercent>
      </DocHeader>
      <ProgressTrack>
        <ProgressBar $percent={progress} />
      </ProgressTrack>
      <DocBody ref={bodyRef} onScroll={handleScroll} tabIndex={0} aria-label="Document body">
        {contentApplied ? (
          <ReadOnlyEditorContent editor={editor} />
        ) : (
          <LoadingWrapper>
            <CircularProgress size={28} />
          </LoadingWrapper>
        )}
      </DocBody>
    </DocCard>
  );
};

const ObservedAcknowledgementDocumentView = observer(AcknowledgementDocumentView);

// TocProvider supplies the context TocNode reads from — without it the ToC node fails to render.
const AcknowledgementDocumentViewWithToc = (props: AcknowledgementDocumentViewProps): React.ReactElement => (
  <TocProvider>
    <ObservedAcknowledgementDocumentView {...props} />
  </TocProvider>
);

export default AcknowledgementDocumentViewWithToc;
